// Webrtc plugin.
import { trackActions, deviceActions } from '../interface/actions'

// Other plugins.
import { logManager } from '../../logs'

// Libraries.
import { call, put, all } from 'redux-saga/effects'
import { isUndefined, isString } from 'lodash/fp'
import BasicError, { webrtcCodes } from '../../errors'

const log = logManager.getLogger('MEDIA')

/**
 * Render Tracks in a specified container.
 * @method renderTracks
 * @param  {Object} action A "render tracks" action.
 */
export function * renderTracks (webRTC, action) {
  const { trackIds, selector, speakerId } = action.payload

  // Get the tracks that are to be rendered.
  const tracks = yield call([webRTC.track, 'getTracks'], trackIds)
  const filteredTracks = tracks.filter(track => !isUndefined(track))
  let payload = {}

  // Each of trackIds, selector and speakerId must be present in order to render a track
  if (
    trackIds &&
    // trackIds is an array
    trackIds instanceof Array &&
    // trackIds is not empty
    trackIds.length > 0 &&
    // selector is not empty
    selector &&
    // selector is a string or a DOM element
    // (note: We can't use instanceof here since elements part of another window are not of the same instance as window.Element)
    (isString(selector) || 'tagName' in selector)
  ) {
    log.info(`Rendering track(s) in element ${selector}.`, trackIds)

    // Render the tracks.
    yield all(filteredTracks.map(track => call([webRTC.renderer, 'renderTrack'], track, selector, speakerId)))

    log.info(
      'Finished rendering track(s).',
      filteredTracks.map(track => track.id)
    )

    payload = { selector }
  } else {
    const message = 'Failed to render tracks. One of trackIds or cssSelector is missing from request.'

    log.info(
      message,
      filteredTracks.map(track => track.id)
    )

    payload.error = new BasicError({
      message,
      code: webrtcCodes.INVALID_PARAM
    })
  }

  // Report operation done.
  yield put(
    trackActions.renderTracksFinish(
      filteredTracks.map(track => track.id),
      payload
    )
  )
}

/**
 * Stop Tracks from being rendered in a specified container.
 * @method removeTracks
 * @param  {Object} action A "remove tracks" action.
 */
export function * removeTracks (webRTC, action) {
  const { trackIds, selector } = action.payload
  log.info(`Removing track(s) from element ${selector}.`, trackIds)

  // Remove the tracks.
  yield all(trackIds.map(id => call([webRTC.renderer, 'unrenderTrack'], id, selector)))

  log.info('Finished removing track(s).', trackIds)
  // Report operation done.
  yield put(
    trackActions.removeTracksFinish(trackIds, {
      selector
    })
  )
}

/**
 * Mute certain tracks.
 * @method muteTracks
 * @param  {Object} action
 * @param  {Array} action.payload A list of track IDs.
 */
export function * muteTracks (webRTC, action) {
  log.info('Muting track(s).', action.payload)
  // Get the tracks that are to be muted.
  const allTracks = yield call([webRTC.track, 'getTracks'])
  const tracks = allTracks.filter(track => action.payload.includes(track.id))

  // Mute the tracks.
  yield all(tracks.map(track => call([track, 'mute'])))

  log.info(
    'Finished muting track(s).',
    tracks.map(track => track.id)
  )
  // Report operation done.
  yield put(trackActions.muteTracksFinish(tracks.map(track => track.id)))
}

/**
 * Unmute certain tracks.
 * @method unmuteTracks
 * @param  {Object} action
 * @param  {Array} action.payload A list of track IDs.
 */
export function * unmuteTracks (webRTC, action) {
  log.info('Unmuting track(s).', action.payload)

  // Get the tracks that are to be unmuted.
  const allTracks = yield call([webRTC.track, 'getTracks'])
  const tracks = allTracks.filter(track => action.payload.includes(track.id))

  // Unmute the tracks.
  yield all(tracks.map(track => call([track, 'unmute'])))

  log.info(
    'Finished unmuting track(s).',
    tracks.map(track => track.id)
  )
  // Report operation done.
  yield put(trackActions.unmuteTracksFinish(tracks.map(track => track.id)))
}

/* Initialize the media devices.
 * @method initializeDevices
 * @param  {Object} action
 * @param  {Oject} action.payload The media constraints
 */
export function * initializeDevices (webRTC, action) {
  // Get the media devices available.
  try {
    const devices = yield call([webRTC.devices, 'setupDeviceInitialization'], action.payload)

    if (devices.error) {
      throw devices
    } else {
      log.info('Successfully initialized devices.', devices)
      yield put(deviceActions.initializeDeviceFinish({ devices }))
    }
  } catch (error) {
    log.info(`Encountered error while initializing devices: ${error.message}.`)
    yield put(deviceActions.initializeDeviceFinish({ error }))
  }
}
